import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {GamePlay} from "../../model/game-play";
import {Router} from "@angular/router";
import {first} from "rxjs";
import {GameService} from "../../services/game.service";
import {GameStatus} from "../../model/gameStatus";
import { Location } from "@angular/common";
import {ODK} from "../../model/odk";
import {HashSimple} from "../../model/hash";
import {PlayType} from "../../model/playType";
import {MessageDialogService} from "../../services/message-dialog.service";
import {PenTeam} from "../../model/penTeam";
import {PlayResult} from "../../model/playResult";
import {DialogKickStatsComponent} from "../dialog-kick-stats/dialog-kick-stats.component";
import {SendStatsDialogComponent} from "../send-stats-dialog/send-stats-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {AuthService} from "../../services/auth.service";
import { MatIcon } from '@angular/material/icon';
import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatHeaderRowDef, MatHeaderRow, MatCellDef, MatCell, MatRowDef, MatRow } from '@angular/material/table';
import { MatSelect, MatOption } from '@angular/material/select';
import { AutoOpenSelectDirective } from '../../directives/auto-open-select.directive';
import { FormsModule } from '@angular/forms';
import { DnDistEditDirective } from '../../directives/dn-dist-edit.directive';
import { MatInput } from '@angular/material/input';
import { AutoFocusInputDirective } from '../../directives/auto-focus-input.directive';
import { PenaltyEditDirective } from '../../directives/penalty-edit.directive';
import { TranslocoModule } from '@ngneat/transloco';
import { FilterOptionsPipe } from '../../pipe/filter-options.pipe';

@Component({
    selector: 'app-play-list',
    templateUrl: './play-list.component.html',
    styleUrls: ['./play-list.component.scss'],
    imports: [MatIcon, MatButtonToggleGroup, MatButtonToggle, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatHeaderRowDef, MatHeaderRow, MatCellDef, MatCell, MatSelect, AutoOpenSelectDirective, FormsModule, MatOption, DnDistEditDirective, MatInput, AutoFocusInputDirective, PenaltyEditDirective, MatRowDef, MatRow, TranslocoModule, FilterOptionsPipe]
})
class PlayListComponent implements OnInit {
  game = inject(GameService);
  private location = inject(Location);
  router = inject(Router);
  messageDialog = inject(MessageDialogService);
  authService = inject(AuthService);

  dialog = inject(MatDialog);
  qtrEnum = [1, 2, 3, 4, 5]
  odkEnum: any = ODK;
  odkKeys = Object.keys(ODK).filter(key => isNaN(Number(key)));
  hashEnum: any = HashSimple;
  hashKeys = Object.keys(HashSimple).filter(key => isNaN(Number(key)));
  playTypeEnum: any = PlayType;
  playResultEnum: any = PlayResult;
  penTeamEnum: any = PenTeam;
  penTeamKeys = Object.keys(PenTeam).filter(key => isNaN(Number(key)));


  baseCols = ['gamePlayNumber', 'odk', 'qtr', 'dnDist', 'yardLine', 'hash', 'playType', 'result', 'gnLs', 'penalty', 'kickRetYds'];
  groupRow = ['gb', 'gh', 'ga', 'gx'];
  previousValue: any = null;
  plays = computed(() => {
    return [...this.game['_gamePlayList']()].sort(
        (a, b) => (b.gamePlayNumber ?? 0) - (a.gamePlayNumber ?? 0)
    );
  });
  homeTeam = signal<string>('Home');
  awayTeam = signal<string>('Away');
  qtrFilter = signal<number | null>(null);
  filteredPlays = computed(() => {
    const q = this.qtrFilter();
    return q == null ? this.plays() : this.plays().filter(p => p.qtr === q);
  });
  editingCell = signal<{ row: number, col: string } | null>(null);
  currentGamePlay = computed(() => {
    if (!this.game.gamePlayList?.length) return null;
    // Tri pour être sûr
    const plays = [...this.game.gamePlayList].sort(
      (a, b) => (a.gamePlayNumber ?? 0) - (b.gamePlayNumber ?? 0)
    );
    return plays[plays.length - 1]; // dernier jeu
  });

  showSendStatsIcon = signal(true)
  showSendHudlIcon = signal(false)

  protected readonly first = first;
  protected readonly GameStatus = GameStatus;
  protected readonly PlayType = PlayType;
  private roleLabels = ['QB', 'WR', 'RB', 'T1', 'T2', 'Ret', 'K', 'Int', 'BD', 'FF', 'RF'];
  private homeKeys = ['passer', 'receiver', 'rusher', 'tackler1', 'tackler2', 'returner', 'kicker', 'interceptedBy', 'battedDown', 'forcedFumbleBy', 'recoveredBy'];
  homeCols = this.homeKeys.map(k => `home_${k}`);
  roleColsHome = this.roleLabels.map((label, i) => ({key: this.homeCols[i], role: this.homeKeys[i], label}));
  private awayKeys = ['oppPasser', 'oppReceiver', 'oppRusher', 'oppTackler1', 'oppTackler2', 'oppReturner', 'oppKicker', 'oppInterceptedBy', 'oppBattedDown', 'oppForcedFumbleBy', 'oppRecoveredBy'];
  awayCols = this.awayKeys.map(k => `away_${k}`);
  roleColsAway = this.roleLabels.map((label, i) => ({key: this.awayCols[i], role: this.awayKeys[i], label}));

  //TODO : Group des PlayType selon les ODK et PlayResult selon PlayType
  //TODO : Blockquer les colonnes que l'on ne devrait pas remplir selon ODK, PlayType et PlayResult

  homeTeamColor = 'rgba(16,185,129,0.35)'; // Couleur par défaut (vert avec transparence)
  awayTeamColor = 'rgba(244,63,94,0.35)'; // Couleur par défaut (rouge avec transparence)
  homeTeamColorSolid = '#10b981'; // Pour les cellules
  awayTeamColorSolid = '#f43f5e'; // Pour les cellules
  homeTeamCellBg = 'rgba(16,185,129,.08)'; // Background des cellules home
  awayTeamCellBg = 'rgba(244,63,94,.08)';

  get columnHeader(): string[] {
    if (!this.game?.game) {
      return []; // ⚡ évite l'erreur au premier rendu
    }
    const cols = [...this.baseCols, ...this.homeCols, ...this.awayCols];
    if (this.game.game.gameStatus !== GameStatus.Completed) {
      cols.push('actions');
    }
    return cols;
  }

  indexOf = (row: GamePlay) => this.filteredPlays().indexOf(row);

  onQtrChange(val: any) {
    this.qtrFilter.set(val == null ? null : Number(val));
  }

  isEditable = (row: GamePlay) => {
    if (this.game.game.gameStatus === GameStatus.Completed) {
      return false;
    }
    if (this.game.game.gameStatus !== GameStatus.InProgress) {
      return true;
    }
    return row.gamePlayNumber !== this.currentGamePlay()?.gamePlayNumber;
  };

  startEdit(rowIndex: number, col: string, initialValue?: any) {
    const row = this.filteredPlays()[rowIndex];

    if (!this.isEditable(row)) {
      return;
    }

    this.previousValue = initialValue;
    this.editingCell.set({row: rowIndex, col});
  }

  stopEdit(row: any, col: string) {
    const value = row[col];

    switch (col) {
      /**
       * 🔹 Validation YardLine
       */
      case 'yardLine': {
        const n = parseInt(value, 10);
        if (isNaN(n) || !((n >= 1 && n <= 55) || (n >= -54 && n <= -1))) {
          this.rollback(row, col, 'La valeur doit être entre 1-55 ou -54 à -1');
          return;
        }
        break;
      }
      case 'down': {
        const n = parseInt(value, 10);
        if (isNaN(n) || n < 1 || n > 4) {
          this.rollback(row, col, 'Le down doit être entre 1 et 4');
          return;
        }
        break;
      }
      case 'distance': {
        const n = parseInt(value, 10);
        if (isNaN(n) || n < 0) {
          this.rollback(row, col, 'La distance doit être un entier positif');
          return;
        }
        break;
      }
      case 'penalty': {
        if (row.penTeam && (!row.penYards || row.penYards <= 0)) {
          this.messageDialog.openMessage(
            'app.errorTitle',
            'validation.gamePlay.penaltyYardageRequiredWhenTeamSelected',
            'app.ok',
            'error'
          );
          row.penTeam = this.previousValue?.penTeam;
        }

        if (!row.penTeam) {
          row.penYards = null;
        }
        break;
      }

      case 'rusher':
      case 'passer':
      case 'receiver':
      case 'tackler1':
      case 'tackler2':
      case 'battedDown':
      case 'forcedFumbleBy':
      case 'returner':
      case 'kicker':
      case 'recoveredBy':
      case 'interceptedBy':
      case 'oppRusher':
      case 'oppPasser':
      case 'oppReceiver':
      case 'oppTackler1':
      case 'oppTackler2':
      case 'oppBattedDown':
      case 'oppForcedFumbleBy':
      case 'oppReturner':
      case 'oppKicker':
      case 'oppRecoveredBy':
      case 'oppInterceptedBy': {
        const n = parseInt(value, 10);
        if (value != null && (isNaN(n) || n < 0 || n > 99)) {
          this.rollback(row, col, 'Le numéro du joueur doit être entre 0 et 99');
          return;
        }
        break;
      }

      /**
       * Validation gnLs (peut être négatif, mais numérique)
       */
      case 'gnLs': {
        if (value != null && isNaN(parseInt(value, 10))) {
          this.rollback(row, col, 'Le gain/perte doit être un nombre valide');
          return;
        }
        break;
      }

      /**
       * Validation penYards (positif uniquement)
       */
      case 'penYards': {
        const n = parseInt(value, 10);
        if (value != null && (isNaN(n) || n < 0)) {
          this.rollback(row, col, 'Les verges de pénalité doivent être ≥ 0');
          return;
        }
        break;
      }

      /**
       * Validation returnYards (peut être négatif ou positif mais numérique)
       */
      case 'returnYards': {
        if (value != null && isNaN(parseInt(value, 10))) {
          this.rollback(row, col, 'Les verges de retour doivent être un nombre');
          return;
        }
        break;
      }
    }

    this.editingCell.set(null);
    this.saveRow(row, col, value);
  }

  rollback(row: any, col: string, message: string) {
    this.messageDialog.openMessage(
      'errorTitle',
      message,
      'ok',
      'error'
    ).subscribe(() => {
      row[col] = this.previousValue;
      this.startEdit(this.indexOf(row), col, row[col]);
    });
  }

  saveRow(row: any, col: string, value: any) {
    console.log(`Sauvegarde demandée pour col=${col}, valeur=${value}`, row);
    this.game.saveExistingGamePlay(row)
  }

  cancelEdit(row: any, col: string) {
    row[col] = this.previousValue;
    this.editingCell.set(null);
  }

  async ngOnInit() {
    if (!this.game.game) {
      const gameInfo = window.localStorage.getItem('gameInfo')
      if (gameInfo) {
        await this.game.selectGame(JSON.parse(gameInfo))
      }
    }

    // Charger les couleurs des équipes
    this.updateTeamInfos();

    if (this.game.game.homeTeam.isPayingTeam && await this.authService.isUserWithPayingTeam()) {
      this.showSendStatsIcon.set(true)
      this.showSendHudlIcon.set(false)
    } else {
      this.showSendStatsIcon.set(false)
      this.showSendHudlIcon.set(true)
    }
  }

  generateStatsPDF() {
    this.game.openStats()
  }

  sendStatsByEmail() {
    const dialogRef = this.dialog.open(SendStatsDialogComponent, {
      data: {
        titleKey: 'sendStats.title',
        descriptionKey: 'sendStats.description',
        cancelKey: 'sendStats.cancel',
        actionBtnKey: 'sendStats.sendStatsBtn'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result.action === "end") {
        this.game.sendGameStats();
      }
    });
  }

  sendHudlDocByEmail() {
    const dialogRef = this.dialog.open(SendStatsDialogComponent, {
      data: {
        titleKey: 'sendHudl.title',
        descriptionKey: 'sendHudl.description',
        cancelKey: 'sendHudl.cancel',
        actionBtnKey: 'sendHudl.sendHudlBtn'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result.action === "end") {
        this.game.sendHudlStats();
      }
    });
  }

  private updateTeamInfos() {
    if (this.game?.game?.homeTeam) {
      this.homeTeam.set(this.game.game.homeTeam.teamName);
      const homeColor = this.game.game.homeTeam.teamColor || '';
      this.homeTeamColorSolid= homeColor
      // Convertir hex en rgba avec transparence (0.35 pour headers, 0.08 pour cellules)
      this.homeTeamColor = this.hexToRgba(homeColor, 0.55);
      this.homeTeamCellBg = this.hexToRgba(homeColor, 0.50);
    }
    if (this.game?.game?.awayTeam) {
      this.awayTeam.set(this.game.game.awayTeam.teamName);
      const awayColor = this.game.game.awayTeam.teamColor || '';
      this.awayTeamColorSolid = awayColor;
      // Convertir hex en rgba avec transparence (0.35 pour headers, 0.08 pour cellules)
      this.awayTeamColor = this.hexToRgba(awayColor, 0.55);
      this.awayTeamCellBg = this.hexToRgba(awayColor, 0.50);
    }
  }

  hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  insertAfter(gamePlayNumber: number) {
    if (gamePlayNumber === 0) {
      return
    }
    const newGamePlay = gamePlayNumber + "b"
    this.router.navigate(['/game-play-list', newGamePlay]);
  }

  back() {
    if (this.isGameInReview()) {
      window.localStorage.removeItem('gameInfo')
      this.router.navigate(['/']);
    }
    this.location.back();
  }

  editPlay(row: GamePlay) {
    if (row._status === 'Pending') return; // cannot edit a pending play
    this.router.navigate(['/game-play-list', row.gamePlayNumber]);
  }

  isGameInReview() {
    return this.game.game?.gameStatus === GameStatus.InReview;
  }

  isGameCompleted() {
    return this.game.game?.gameStatus === GameStatus.Completed;
  }

  reopenGame() {
    this.messageDialog.openConfirm(
      'reopenGame.title',
      'reopenGame.description',
      'reopenGame.confirmBtn',
      'reopenGame.cancel'
    ).subscribe(result => {
      if (result?.action === 'confirm') {
        this.game.changeGameStatus(GameStatus.InReview);
      }
    });
  }

  openKickReturnDialog(row:GamePlay) {
    if (row.odk !== ODK.Kick)
      return this.messageDialog.openMessage(
        'validation.errorTitle',
        'validation.gamePlay.playMustBeAKick',
        'app.ok',
        'error',
      )
    
    // Passer la liste des plays triés pour permettre de trouver la row suivante
    const sortedPlays = [...this.game['_gamePlayList']()].sort(
      (a, b) => (a.gamePlayNumber ?? 0) - (b.gamePlayNumber ?? 0)
    );
    
    const kickStat = this.dialog.open(DialogKickStatsComponent, {
      width: '500px',
      data: {row, plays: sortedPlays}
    })

     kickStat.afterClosed().subscribe((result) => {
      if (result) {
        // Les valeurs sont déjà mises à jour dans result.row par le dialog
        this.game.saveExistingGamePlay(row);
      }
    });

    return 11

  }
}

export default PlayListComponent
